/**
 * Admin > Hero Banners tab.
 *
 * Lets an admin manage an ARBITRARY number of hero banners displayed on the
 * home page. Each row is edited in-place (image + tag + title + subtitle +
 * CTA + href). "Add banner" appends a fresh row; "Delete" removes one.
 *
 * The parent (AdminPage) owns the `hero` array + mutations; this component
 * is a dumb presenter with three callbacks: onUpdate / onAdd / onDelete.
 */
import React from 'react';
import { Plus, Trash2, Info } from 'lucide-react';
import ImageUpload from './ImageUpload';
import EditableText from './EditableText';

const HeroBannersAdmin = ({ hero, onUpdate, onAdd, onDelete }) => (
  <div className="space-y-4" data-testid="admin-hero-banners">
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <h2 className="text-xl font-black text-[#12263A]">Hero banners <span className="text-neutral-400 font-semibold">({hero.length})</span></h2>
        <p className="text-xs text-neutral-600 mt-1 max-w-2xl flex items-start gap-1">
          <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
          These banners cycle on the home page. Slide duration lives in <b className="ml-1">Site settings → Hero carousel</b>.
        </p>
      </div>
      <button
        onClick={onAdd}
        data-testid="hero-banner-add-btn"
        className="bg-[#F46A25] hover:bg-[#e05a15] text-white text-sm font-bold px-4 py-2 rounded-sm flex items-center gap-2 flex-shrink-0"
      >
        <Plus className="w-4 h-4" /> Add banner
      </button>
    </div>

    {hero.length === 0 && (
      <div className="bg-white border border-dashed border-neutral-300 rounded-sm p-10 text-center text-neutral-500">
        No hero banners yet. Click <b>Add banner</b> to create your first one.
      </div>
    )}

    {hero.map((h, i) => (
      <div
        key={h.id}
        className="bg-white border border-neutral-200 rounded-sm p-4 grid grid-cols-1 md:grid-cols-[300px_1fr] gap-4 relative"
        data-testid={`hero-banner-${h.id}`}
      >
        <button
          onClick={() => onDelete(h.id)}
          data-testid={`hero-banner-delete-${h.id}`}
          className="absolute top-2 right-2 text-xs text-red-600 hover:text-white hover:bg-red-600 border border-red-200 hover:border-red-600 font-bold px-2 py-1 rounded-sm flex items-center gap-1 transition-colors z-10"
          title="Delete this banner"
        >
          <Trash2 className="w-3 h-3" /> Delete
        </button>
        <ImageUpload
          src={h.image}
          alt={h.title}
          ratio="aspect-video"
          onChange={(u) => onUpdate(h.id, { image: u })}
        />
        <div className="space-y-2 text-sm">
          <div className="text-[10px] uppercase text-neutral-400 font-bold">Banner #{i + 1}</div>
          <div>
            <span className="text-[11px] uppercase text-neutral-500 font-semibold">Tag (badge)</span>
            <EditableText value={h.tag} onSave={(v) => onUpdate(h.id, { tag: v })} />
          </div>
          <div>
            <span className="text-[11px] uppercase text-neutral-500 font-semibold">Title</span>
            <EditableText
              value={h.title}
              onSave={(v) => onUpdate(h.id, { title: v })}
              className="font-bold text-lg"
            />
          </div>
          <div>
            <span className="text-[11px] uppercase text-neutral-500 font-semibold">Subtitle</span>
            <EditableText value={h.subtitle} onSave={(v) => onUpdate(h.id, { subtitle: v })} />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <span className="text-[11px] uppercase text-neutral-500 font-semibold">Button text</span>
              <EditableText value={h.cta} onSave={(v) => onUpdate(h.id, { cta: v })} />
            </div>
            <div className="flex-1">
              <span className="text-[11px] uppercase text-neutral-500 font-semibold">Link (href)</span>
              <EditableText
                value={h.href}
                onSave={(v) => onUpdate(h.id, { href: v })}
                className="font-mono text-xs"
              />
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

export default HeroBannersAdmin;
